import type {
  AvailabilitySlot,
  BookingOffering,
  StudentLessonSchedule,
  StudentLessonScheduleRow,
} from "./api.js";

export type BookingPreferenceEvidence = "recent-bookings" | "assigned-tutor";

export interface StudentBookingPreference {
  providerId: number;
  serviceId: number;
  tutorName: string | null;
  serviceName: string | null;
  recommendedDurationMinutes: number | null;
  usualWeekday: number | null;
  usualLocalTime: string | null;
  evidence: BookingPreferenceEvidence;
}

const LONDON_TIME_ZONE = "Europe/London";
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MINUTES = 7 * 24 * 60;
const PAST_EVIDENCE_DAYS = 180;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

const londonFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: LONDON_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

interface LondonParts {
  year: number;
  month: number;
  day: number;
  weekday: number;
  hour: number;
  minute: number;
}

function londonParts(value: string): LondonParts | null {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  const parts = londonFormatter.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  const weekday = WEEKDAYS.indexOf(part("weekday") as (typeof WEEKDAYS)[number]);
  const year = Number(part("year"));
  const month = Number(part("month"));
  const day = Number(part("day"));
  const hour = Number(part("hour"));
  const minute = Number(part("minute"));

  if (
    weekday < 0 ||
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    !Number.isInteger(hour) ||
    !Number.isInteger(minute)
  ) {
    return null;
  }

  return { year, month, day, weekday, hour, minute };
}

function offeringKey(providerId: number, serviceId: number): string {
  return `${providerId}:${serviceId}`;
}

function validOffering(offering: BookingOffering): boolean {
  return offering.book_with_credit.allowed || offering.request_onsite.allowed;
}

function confirmedRecommendationRow(row: StudentLessonScheduleRow): boolean {
  return row.status === "approved";
}

function rowIsRecent(row: StudentLessonScheduleRow, now: Date): boolean {
  const timestamp = Date.parse(row.startsAt);
  if (!Number.isFinite(timestamp)) return false;
  return timestamp >= now.getTime() - PAST_EVIDENCE_DAYS * DAY_MS && timestamp <= now.getTime();
}

function chooseMostCommonNumber(
  rows: readonly StudentLessonScheduleRow[],
  valueOf: (row: StudentLessonScheduleRow) => number | null,
): number | null {
  const stats = new Map<number, { count: number; latest: number }>();
  for (const row of rows) {
    const value = valueOf(row);
    if (value === null || !Number.isFinite(value)) continue;
    const timestamp = Date.parse(row.startsAt) || 0;
    const current = stats.get(value) ?? { count: 0, latest: 0 };
    stats.set(value, {
      count: current.count + 1,
      latest: Math.max(current.latest, timestamp),
    });
  }

  const ranked = [...stats.entries()].sort(
    ([valueA, a], [valueB, b]) =>
      b.count - a.count || b.latest - a.latest || valueA - valueB,
  );
  return ranked[0]?.[0] ?? null;
}

function lessonDurationMinutes(row: StudentLessonScheduleRow): number | null {
  const duration = (Date.parse(row.endsAt) - Date.parse(row.startsAt)) / 60_000;
  return Number.isFinite(duration) && duration > 0 ? Math.round(duration) : null;
}

function lessonWeekday(row: StudentLessonScheduleRow): number | null {
  return londonParts(row.startsAt)?.weekday ?? null;
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? null;
}

function usualLocalTime(
  rows: readonly StudentLessonScheduleRow[],
  usualWeekday: number | null,
): string | null {
  if (usualWeekday === null) return null;
  const minutes = rows.flatMap((row) => {
    const parts = londonParts(row.startsAt);
    return parts && parts.weekday === usualWeekday ? [parts.hour * 60 + parts.minute] : [];
  });
  const middle = median(minutes);
  if (middle === null) return null;
  const hour = Math.floor(middle / 60);
  const minute = middle % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/**
 * Derive presentation-only booking preferences from recent confirmed lesson history.
 *
 * Every candidate pair is first intersected with WMF-SIte's current offerings. A historical
 * lesson can therefore influence ranking, but can never create a tutor/service choice that the
 * booking authority did not return. Multi-service students receive one independent preference
 * per service instead of one universal "usual lesson". Future booked lessons are deliberately
 * excluded here: they can warn about a duplicate week, but they are not evidence of a usual
 * historical pattern until they have actually happened.
 */
export function deriveStudentBookingPreferences(
  offerings: readonly BookingOffering[],
  schedule: StudentLessonSchedule | null,
  now = new Date(),
): StudentBookingPreference[] {
  const available = offerings.filter(validOffering);
  if (available.length === 0) return [];

  const validPairs = new Set(
    available.map((offering) => offeringKey(offering.provider_id, offering.service_id)),
  );
  const recentRows = schedule
    ? schedule.past.filter(
        (row) =>
          confirmedRecommendationRow(row) &&
          rowIsRecent(row, now) &&
          row.providerId !== null &&
          row.serviceId !== null &&
          validPairs.has(offeringKey(row.providerId, row.serviceId)),
      )
    : [];

  const serviceIds = [...new Set(available.map((offering) => offering.service_id))].sort(
    (a, b) => a - b,
  );

  return serviceIds.flatMap<StudentBookingPreference>((serviceId) => {
    const serviceOfferings = available.filter((offering) => offering.service_id === serviceId);
    const serviceRows = recentRows.filter((row) => row.serviceId === serviceId);

    if (serviceRows.length > 0) {
      const providerId = chooseMostCommonNumber(serviceRows, (row) => row.providerId);
      const offering = serviceOfferings.find((candidate) => candidate.provider_id === providerId);
      if (!offering || providerId === null) return [];

      const pairRows = serviceRows.filter((row) => row.providerId === providerId);
      const usualWeekday = chooseMostCommonNumber(pairRows, lessonWeekday);
      const latest = [...pairRows].sort(
        (a, b) => (Date.parse(b.startsAt) || 0) - (Date.parse(a.startsAt) || 0),
      )[0];

      return [
        {
          providerId,
          serviceId,
          tutorName: offering.tutor_name ?? latest?.tutorName ?? null,
          serviceName: offering.service_name ?? latest?.serviceName ?? null,
          recommendedDurationMinutes: chooseMostCommonNumber(pairRows, lessonDurationMinutes),
          usualWeekday,
          usualLocalTime: usualLocalTime(pairRows, usualWeekday),
          evidence: "recent-bookings" as const,
        },
      ];
    }

    const assigned = serviceOfferings.filter((offering) =>
      offering.sources.includes("hub_relationship"),
    );
    if (assigned.length !== 1) return [];
    const offering = assigned[0]!;
    return [
      {
        providerId: offering.provider_id,
        serviceId,
        tutorName: offering.tutor_name ?? null,
        serviceName: offering.service_name ?? null,
        recommendedDurationMinutes: null,
        usualWeekday: null,
        usualLocalTime: null,
        evidence: "assigned-tutor" as const,
      },
    ];
  });
}

export function bookingPreferenceForOffering(
  preferences: readonly StudentBookingPreference[],
  offering: Pick<BookingOffering, "provider_id" | "service_id">,
): StudentBookingPreference | null {
  return (
    preferences.find(
      (preference) =>
        preference.providerId === offering.provider_id &&
        preference.serviceId === offering.service_id,
    ) ?? null
  );
}

function localMinuteOfWeek(value: string): number | null {
  const parts = londonParts(value);
  return parts ? parts.weekday * 24 * 60 + parts.hour * 60 + parts.minute : null;
}

function preferenceMinuteOfWeek(preference: StudentBookingPreference): number | null {
  if (preference.usualWeekday === null || preference.usualLocalTime === null) return null;
  const match = /^(\d{2}):(\d{2})$/.exec(preference.usualLocalTime);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return preference.usualWeekday * 24 * 60 + hour * 60 + minute;
}


function calendarDateKey(parts: LondonParts): string {
  return String(parts.year).padStart(4, "0") + "-" +
    String(parts.month).padStart(2, "0") + "-" +
    String(parts.day).padStart(2, "0");
}

function calendarDateOffset(day: string, offset: number): string {
  const parts = day.split("-");
  const yearText = parts[0];
  const monthText = parts[1];
  const dateText = parts[2];
  if (!yearText || !monthText || !dateText || parts.length !== 3) return day;

  const year = Number(yearText);
  const month = Number(monthText);
  const date = Number(dateText);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(date)) return day;
  if (month < 1 || month > 12 || date < 1 || date > 31) return day;

  return new Date(Date.UTC(year, month - 1, date + offset)).toISOString().slice(0, 10);
}

function calendarDateDistance(left: string, right: string): number | null {
  const leftTime = Date.parse(left + "T00:00:00.000Z");
  const rightTime = Date.parse(right + "T00:00:00.000Z");
  if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) return null;
  return Math.round((leftTime - rightTime) / DAY_MS);
}

/**
 * Return a quick-booking pattern only when at least two recent confirmed rows
 * agree on the currently offered tutor, service, length and London wall time.
 * Any disagreement leaves the learner on the explicit choice flow.
 */
export function unambiguousUsualBookingPreference(
  offerings: readonly BookingOffering[],
  schedule: StudentLessonSchedule | null,
  selected: BookingOffering,
  now = new Date(),
): StudentBookingPreference | null {
  if (!schedule || !validOffering(selected)) return null;

  const serviceOfferings = offerings.filter(
    (offering) => validOffering(offering) && offering.service_id === selected.service_id,
  );
  const selectedOffers = serviceOfferings.filter(
    (offering) => offering.provider_id === selected.provider_id,
  );
  if (selectedOffers.length !== 1) return null;

  const offeredPairs = new Set(
    serviceOfferings.map((offering) => offeringKey(offering.provider_id, offering.service_id)),
  );
  const evidence = schedule.past.filter(
    (row) =>
      confirmedRecommendationRow(row) &&
      rowIsRecent(row, now) &&
      row.serviceId === selected.service_id &&
      row.providerId !== null &&
      offeredPairs.has(offeringKey(row.providerId, row.serviceId)),
  );
  if (
    evidence.length < 2 ||
    evidence.some((row) => row.providerId !== selected.provider_id)
  ) {
    return null;
  }

  const first = evidence[0];
  if (!first) return null;
  const firstDuration = lessonDurationMinutes(first);
  const firstParts = londonParts(first.startsAt);
  if (firstDuration === null || firstParts === null) return null;

  for (const row of evidence) {
    const duration = lessonDurationMinutes(row);
    const parts = londonParts(row.startsAt);
    if (
      duration !== firstDuration ||
      parts === null ||
      parts.weekday !== firstParts.weekday ||
      parts.hour !== firstParts.hour ||
      parts.minute !== firstParts.minute
    ) {
      return null;
    }
  }

  return {
    providerId: selected.provider_id,
    serviceId: selected.service_id,
    tutorName: selected.tutor_name ?? first.tutorName,
    serviceName: selected.service_name ?? first.serviceName,
    recommendedDurationMinutes: firstDuration,
    usualWeekday: firstParts.weekday,
    usualLocalTime: String(firstParts.hour).padStart(2, "0") + ":" +
      String(firstParts.minute).padStart(2, "0"),
    evidence: "recent-bookings",
  };
}

export function slotMatchesUsualPreference(
  slot: AvailabilitySlot,
  preference: StudentBookingPreference,
): boolean {
  const startsAt = Date.parse(slot.starts_at);
  const endsAt = Date.parse(slot.ends_at);
  const parts = londonParts(slot.starts_at);
  const duration = (endsAt - startsAt) / 60_000;
  return Number.isFinite(startsAt) &&
    Number.isFinite(endsAt) &&
    slot.provider_id === preference.providerId &&
    slot.service_id === preference.serviceId &&
    Math.round(duration) === preference.recommendedDurationMinutes &&
    parts !== null &&
    parts.weekday === preference.usualWeekday &&
    String(parts.hour).padStart(2, "0") + ":" +
      String(parts.minute).padStart(2, "0") === preference.usualLocalTime;
}

/** The start of the learner's latest approved lesson of this service that is still to come. */
export function latestConfirmedUpcomingLessonStart(
  schedule: StudentLessonSchedule | null,
  preference: Pick<StudentBookingPreference, "serviceId"> | null,
  now = new Date(),
): string | null {
  if (!schedule || !preference) return null;
  let latest: string | null = null;
  for (const lesson of schedule.upcoming) {
    const startsAt = Date.parse(lesson.startsAt);
    if (
      lesson.status !== "approved" ||
      lesson.serviceId !== preference.serviceId ||
      !Number.isFinite(startsAt) ||
      startsAt <= now.getTime()
    ) continue;
    if (latest === null || startsAt > Date.parse(latest)) latest = lesson.startsAt;
  }
  return latest;
}

/**
 * Keep the shortcut inside the next occurrence's same or adjacent London day.
 * Every result is an existing authority-returned slot; duration, tutor and
 * service must also match the confirmed historical pattern.
 */
export function nearestUsualAvailabilitySlots(
  slots: readonly AvailabilitySlot[],
  preference: StudentBookingPreference | null,
  now = new Date(),
  limit = 3,
  /**
   * Start of the learner's latest confirmed upcoming lesson of this service.
   * The usual time that week is already taken, so the shortcut aims at the
   * first usual weekday/time after it instead of offering that week again.
   */
  latestBookedStartsAt: string | null = null,
): AvailabilitySlot[] {
  const weekMinute = preference ? preferenceMinuteOfWeek(preference) : null;
  if (
    !preference ||
    preference.recommendedDurationMinutes === null ||
    weekMinute === null ||
    !Number.isFinite(now.getTime())
  ) {
    return [];
  }

  const bookedUntil = latestBookedStartsAt === null ? Number.NaN : Date.parse(latestBookedStartsAt);
  const reference = Number.isFinite(bookedUntil) && bookedUntil > now.getTime() ? new Date(bookedUntil) : now;
  const current = londonParts(reference.toISOString());
  const usualWeekday = preference.usualWeekday;
  if (!current || usualWeekday === null) return [];
  const usualMinute = weekMinute % (24 * 60);
  let daysUntilUsual = (usualWeekday - current.weekday + 7) % 7;
  if (daysUntilUsual === 0 && current.hour * 60 + current.minute >= usualMinute) {
    daysUntilUsual += 7;
  }
  const targetDate = calendarDateOffset(calendarDateKey(current), daysUntilUsual);

  return slots
    .flatMap((slot) => {
      const startsAt = Date.parse(slot.starts_at);
      const endsAt = Date.parse(slot.ends_at);
      const parts = londonParts(slot.starts_at);
      const date = parts ? calendarDateKey(parts) : null;
      const dayDistance = date === null ? null : calendarDateDistance(date, targetDate);
      const duration = (endsAt - startsAt) / 60_000;
      if (
        !Number.isFinite(startsAt) ||
        !Number.isFinite(endsAt) ||
        startsAt <= now.getTime() ||
        !parts ||
        dayDistance === null ||
        Math.abs(dayDistance) > 1 ||
        slot.provider_id !== preference.providerId ||
        slot.service_id !== preference.serviceId ||
        Math.round(duration) !== preference.recommendedDurationMinutes
      ) {
        return [];
      }
      const timeDistance = Math.abs(parts.hour * 60 + parts.minute - usualMinute);
      return [{ slot, distance: Math.abs(dayDistance) * 24 * 60 + timeDistance }];
    })
    .sort((left, right) =>
      left.distance - right.distance ||
      Date.parse(left.slot.starts_at) - Date.parse(right.slot.starts_at),
    )
    .slice(0, Math.max(0, Math.floor(limit)))
    .map(({ slot }) => slot);
}

function weeklyDistance(a: number, b: number): number {
  const direct = Math.abs(a - b);
  return Math.min(direct, WEEK_MINUTES - direct);
}

/** Rank only slots already returned by WMF-SIte; never create or broaden availability. */
export function rankAvailabilitySlots(
  slots: readonly AvailabilitySlot[],
  preference: StudentBookingPreference | null,
): AvailabilitySlot[] {
  const target = preference ? preferenceMinuteOfWeek(preference) : null;
  return [...slots].sort((a, b) => {
    if (target !== null) {
      const aMinute = localMinuteOfWeek(a.starts_at);
      const bMinute = localMinuteOfWeek(b.starts_at);
      const aDistance = aMinute === null ? Number.POSITIVE_INFINITY : weeklyDistance(aMinute, target);
      const bDistance = bMinute === null ? Number.POSITIVE_INFINITY : weeklyDistance(bMinute, target);
      if (aDistance !== bDistance) return aDistance - bDistance;
    }
    return Date.parse(a.starts_at) - Date.parse(b.starts_at);
  });
}

export function londonTeachingWeekStart(value: string): string | null {
  const parts = londonParts(value);
  if (!parts) return null;
  const localDateAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day);
  return new Date(localDateAsUtc - parts.weekday * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Find a confirmed lesson for the same service in the selected slot's London Monday-Sunday week.
 * Matching by service, rather than merely student, avoids false warnings for multi-instrument
 * students. Cancelled, rejected and pending lessons are deliberately not warnings.
 */
export function confirmedLessonInSameTeachingWeek(
  schedule: StudentLessonSchedule | null,
  slot: Pick<AvailabilitySlot, "starts_at" | "service_id">,
): StudentLessonScheduleRow | null {
  if (!schedule) return null;
  const selectedWeek = londonTeachingWeekStart(slot.starts_at);
  if (!selectedWeek) return null;

  return (
    [...schedule.upcoming, ...schedule.past].find(
      (lesson) =>
        lesson.status === "approved" &&
        lesson.serviceId === slot.service_id &&
        londonTeachingWeekStart(lesson.startsAt) === selectedWeek,
    ) ?? null
  );
}
